/*
 * Main JavaScript function for Absenteeism Charts
 */

// Theme color variables (global scope for MutationObserver access)
const darkModeBGColor = '#212529';
const darkModeBorderColor = '#495057';
const darkModeTextColor = '#adb5bd';

const lightModeBGColor = '#fff';
const lightModeBorderColor = '#ccc';
const lightModeTextColor = '#495057';

// Chart series colors
const satisfactoryBGColor = '#729364';
const atRiskBGColor = '#F3DF89';
const moderateBGColor = '#EBA70E';
const severeBGColor = '#C12637';
const chronicBGColor = '#9810f2';

// Category definitions for legend tooltips
const categoryDefinitions = {
    'Satisfactory': 'Students missing less than 5% of school time',
    'At Risk': 'Students missing between 5-10% of school time',
    'Moderate': 'Students missing 10-20% of school time',
    'Severe': 'Students missing 20% or more of school time',
    'Chronic Absence Rate': 'Combined total of Moderate and Severe categories'
};

/**
 * Build a custom HTML legend with Bootstrap tooltips for category definitions
 *
 * @param {HTMLElement} container - The container element to render the legend into
 * @param {Array} seriesConfig - Array of {name, color} objects for each series
 * @param {Object} chartInstance - The eCharts instance for toggle interaction
 * @param {Object} options - Optional settings: {cohortName, isPopover, selectedState}
 */
function buildCustomLegend(container, seriesConfig, chartInstance, options) {
    options = options || {};
    if (!container || !chartInstance) return;

    var legendWrap = document.createElement('div');
    legendWrap.className = 'chart-legend-items';
    if (options.isPopover) {
        legendWrap.classList.add('chart-legend-popover');
    }
    legendWrap.setAttribute('role', 'group');
    legendWrap.setAttribute('aria-label', options.cohortName
        ? options.cohortName + ' legend'
        : 'Chart legend');

    if (options.cohortName) {
        var heading = document.createElement('span');
        heading.className = 'chart-legend-cohort-label';
        heading.style.color = options.cohortColor || 'inherit';
        heading.textContent = options.cohortName + ':';
        legendWrap.appendChild(heading);
    }

    // Get initial selected state from chart instance
    var selectedState = options.selectedState || {};

    seriesConfig.forEach(function(item) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'chart-legend-item';

        // Apply initial inactive state if series is deselected
        if (selectedState[item.name] === false) {
            btn.classList.add('chart-legend-item--inactive');
        }

        // Determine the base category name for the tooltip
        var baseName = item.name;
        if (baseName.indexOf(' - ') !== -1) {
            baseName = baseName.split(' - ')[1];
        }

        var definition = categoryDefinitions[baseName] || '';
        if (definition) {
            btn.setAttribute('data-legend-tooltip', 'true');
            btn.setAttribute('data-bs-placement', 'top');
            btn.setAttribute('data-bs-custom-class', 'custom-tooltip');
            btn.setAttribute('title', definition);
        }

        var swatch = document.createElement('span');
        swatch.className = 'chart-legend-swatch';
        swatch.style.backgroundColor = item.color;
        swatch.setAttribute('aria-hidden', 'true');

        var label = document.createElement('span');
        label.className = 'chart-legend-label';
        label.textContent = baseName;

        btn.appendChild(swatch);
        btn.appendChild(label);

        // Toggle series on click and dismiss tooltip
        btn.addEventListener('click', function() {
            var tip = bootstrap.Tooltip.getInstance(btn);
            if (tip) {
                tip.hide();
            }
            chartInstance.dispatchAction({
                type: 'legendToggleSelect',
                name: item.name
            });
            btn.blur();
        });

        legendWrap.appendChild(btn);
    });

    container.innerHTML = '';
    container.appendChild(legendWrap);

    // Sync visual state when legend selection changes
    chartInstance.on('legendselectchanged', function(params) {
        var buttons = container.querySelectorAll('.chart-legend-item');
        buttons.forEach(function(btn) {
            var labelEl = btn.querySelector('.chart-legend-label');
            if (!labelEl) return;
            var displayName = labelEl.textContent;

            // Find matching series name
            var matchedSeries = seriesConfig.find(function(s) {
                var sName = s.name;
                if (sName.indexOf(' - ') !== -1) {
                    sName = sName.split(' - ')[1];
                }
                return sName === displayName;
            });

            if (matchedSeries && params.selected[matchedSeries.name] === false) {
                btn.classList.add('chart-legend-item--inactive');
            } else {
                btn.classList.remove('chart-legend-item--inactive');
            }
        });
    });

    // Initialize Bootstrap tooltips on legend buttons (hover only to avoid sticky tooltips on click)
    var tooltipTriggers = legendWrap.querySelectorAll('[data-legend-tooltip]');
    tooltipTriggers.forEach(function(el) {
        new bootstrap.Tooltip(el, { trigger: 'hover' });
    });
}

/**
 * Toggle fullscreen mode for a chart container
 * @param {HTMLElement} chartElement - The chart container element
 * @param {Object} chartInstance - The eCharts instance
 */
function toggleChartFullscreen(chartElement, chartInstance) {
    if (!document.fullscreenElement) {
        chartElement.requestFullscreen().then(() => {
            chartInstance.resize();
        }).catch(err => {
            console.warn('Fullscreen request failed:', err);
        });
    } else {
        document.exitFullscreen();
    }
}

function initAbsenceChart(chartId, chartData, regionName, isPopover = false) {

    // All Districts Chart - Stacked Line Chart (Percentage-based)
    let weeklyData = chartData;

    const isDarkMode = document.documentElement.getAttribute('data-bs-theme') === 'dark';

    // Auto-detect if we're in a popover by checking container dimensions or parent
    const chartElement = document.getElementById(chartId);
    if (!isPopover && chartElement) {
        // Check if any parent has class 'popover' or if chart container is small
        const inPopover = !!chartElement.closest('.popover');
        const isSmallContainer = chartElement.offsetWidth < 400;
        isPopover = isPopover || inPopover || isSmallContainer;
    }
    
    // Sort data chronologically by week_of (ISO 8601 date strings)
    weeklyData.sort((a, b) => new Date(a.week_of) - new Date(b.week_of));

    // Prepare arrays for chart
    let weeks = weeklyData.map(item => item.week_display);
    
    // Calculate percentages based on enrollment totals
    let atRiskPercent = weeklyData.map(item => parseFloat((item.at_risk_total / item.enrolled_total * 100).toFixed(1)));
    let moderatePercent = weeklyData.map(item => parseFloat((item.moderate_total / item.enrolled_total * 100).toFixed(1)));
    let severePercent = weeklyData.map(item => parseFloat((item.severe_total / item.enrolled_total * 100).toFixed(1)));
    let satisfactoryPercent = weeklyData.map(item => parseFloat((item.satisfactory_total / item.enrolled_total * 100).toFixed(1)));
    let chronicPercent = weeklyData.map(item => parseFloat((item.chronic_total / item.enrolled_total * 100).toFixed(1)));

    // Initialize the chart
    const chart = echarts.init(document.getElementById(chartId));
    
    // Axis line color
    const axisLineColor = isDarkMode ? darkModeBorderColor : '#999';

    const option = {
        tooltip: {
            trigger: 'axis',
            backgroundColor: isDarkMode ? darkModeBGColor : lightModeBGColor,
            borderColor: isDarkMode ? darkModeBorderColor : lightModeBorderColor,
            borderWidth: 1,
            textStyle: {
                fontSize: isPopover ? 11 : 13,
                color: isDarkMode ? darkModeTextColor : lightModeTextColor
            },
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: '#6a7985'
                }
            },
            formatter: function(params) {
                const dataIndex = params[0].dataIndex;
                const item = weeklyData[dataIndex];

                // Create a mapping of visible series names
                const visibleSeries = {};
                params.forEach(param => {
                    visibleSeries[param.seriesName] = true;
                });

                let tooltipText = `<strong>${item.week_display}</strong><br/>`;
                if (item.district_count) {
                    tooltipText += item.district_count.toLocaleString() + (item.district_count === 1 ? ' School and District' : ' Schools and Districts') + ' Reporting';
                }
                tooltipText += `<hr class="my-1"/>`;

                // Only add series information if it's currently visible
                if (visibleSeries['Satisfactory']) {
                    tooltipText += `<span style="display:inline-block;width:10px;height:10px;background-color:${satisfactoryBGColor};border-radius:50%;margin-right:5px;"></span>`;
                    tooltipText += `Satisfactory: <strong>${(item.satisfactory_total/item.enrolled_total*100).toFixed(1)}%</strong><br/>`;
                }
                if (visibleSeries['At Risk']) {
                    tooltipText += `<span style="display:inline-block;width:10px;height:10px;background-color:${atRiskBGColor};border-radius:50%;margin-right:5px;"></span>`;
                    tooltipText += `At Risk: <strong>${(item.at_risk_total/item.enrolled_total*100).toFixed(1)}%</strong><br/>`;
                }
                if (visibleSeries['Moderate']) {
                    tooltipText += `<span style="display:inline-block;width:10px;height:10px;background-color:${moderateBGColor};border-radius:50%;margin-right:5px;"></span>`;
                    tooltipText += `Moderate: <strong>${(item.moderate_total/item.enrolled_total*100).toFixed(1)}%</strong><br/>`;
                }
                if (visibleSeries['Severe']) {
                    tooltipText += `<span style="display:inline-block;width:10px;height:10px;background-color:${severeBGColor};border-radius:50%;margin-right:5px;"></span>`;
                    tooltipText += `Severe: <strong>${(item.severe_total/item.enrolled_total*100).toFixed(1)}%</strong><br/>`;
                }
                if (visibleSeries['Chronic Absence Rate']) {
                    tooltipText += `<span style="display:inline-block;width:10px;height:10px;background-color:${chronicBGColor};border-radius:50%;margin-right:5px;"></span>`;
                    tooltipText += `Chronic Absence Rate: <strong>${(item.chronic_total/item.enrolled_total*100).toFixed(1)}%</strong>`;
                }

                return tooltipText;
            }
        },
        legend: {
            show: false,
            data: ['Satisfactory', 'At Risk', 'Moderate', 'Severe', 'Chronic Absence Rate'],
            selected: {
                'Satisfactory': false,
                'At Risk': false,
                'Moderate': false,
                'Severe': false,
                'Chronic Absence Rate': true
            }
        },
        grid: {
            left: isPopover ? 5 : 37,
            right: isPopover ? 5 : 30,
            bottom: isPopover ? 0 : 28,
            top: isPopover ? 35 : 48,
            containLabel: true
        },
        title: {
            left: 'center',
            top: isPopover ? 5 : 10,
            //text: regionName + ' Absences',
            text: regionName,
            textStyle: {
                fontSize: isPopover ? 12 : 18,
                color: isDarkMode ? darkModeTextColor : lightModeTextColor,
                fontWeight: 'bold',
                overflow: 'truncate',
                width: isPopover ? 280 : null,
            }
        },
        xAxis: {
            type: 'category',
            name: isPopover ? '' : 'Week Of',
            nameLocation: 'middle',
            nameGap: 54,
            nameTextStyle: {
                color: isDarkMode ? darkModeTextColor : lightModeTextColor
            },
            data: weeks,
            boundaryGap: false,
            axisLabel: {
                formatter: '{value}',
                fontSize: isPopover ? 9 : 11,
                color: isDarkMode ? darkModeTextColor : lightModeTextColor,
                rotate: 45
            },
            axisLine: {
                lineStyle: {
                    color: axisLineColor
                }
            }
        },
        yAxis: {
            type: 'value',
            name: isPopover ? '' : 'Percentage',
            nameLocation: 'middle',
            nameGap: 42,
            nameTextStyle: {
                color: isDarkMode ? darkModeTextColor : lightModeTextColor
            },
            min: 0,
            max: 100,
            axisLabel: {
                formatter: '{value}%',
                fontSize: isPopover ? 9 : 11,
                color: isDarkMode ? darkModeTextColor : lightModeTextColor
            },
            splitLine: {
                lineStyle: {
                    type: 'dashed',
                    color: isDarkMode ? darkModeBorderColor : '#ddd'
                }
            },
            axisLine: {
                lineStyle: {
                    color: axisLineColor
                }
            }
        },
        series: [
            {
                name: 'Satisfactory',
                data: satisfactoryPercent,
                type: 'line',
                connectNulls: false,
                triggerLineEvent: true,
                lineStyle: {
                    width: 1
                },
                itemStyle: {
                    color: satisfactoryBGColor,
                },
                symbolSize: isPopover ? 0 : 6,
                emphasis: {
                    focus: 'series',
                    blurScope: 'coordinateSystem',
                    scale: 1.3,
                    itemStyle: {
                        borderWidth: 2,
                        borderColor: satisfactoryBGColor
                    },
                    lineStyle: {
                        width: 3
                    }
                }
            },
            {
                name: 'At Risk',
                data: atRiskPercent,
                type: 'line',
                connectNulls: false,
                triggerLineEvent: true,
                lineStyle: {
                    width: 1
                },
                itemStyle: {
                    color: atRiskBGColor,
                },
                symbolSize: isPopover ? 0 : 6,
                emphasis: {
                    focus: 'series',
                    blurScope: 'coordinateSystem',
                    scale: 1.3,
                    itemStyle: {
                        borderWidth: 2,
                        borderColor: atRiskBGColor
                    },
                    lineStyle: {
                        width: 3
                    }
                }
            },
            {
                name: 'Moderate',
                data: moderatePercent,
                type: 'line',
                connectNulls: false,
                triggerLineEvent: true,
                lineStyle: {
                    width: 1
                },
                itemStyle: {
                    color: moderateBGColor
                },
                symbolSize: isPopover ? 0 : 6,
                emphasis: {
                    focus: 'series',
                    blurScope: 'coordinateSystem',
                    scale: 1.3,
                    itemStyle: {
                        borderWidth: 2,
                        borderColor: moderateBGColor
                    },
                    lineStyle: {
                        width: 3
                    }
                }
            },
            {
                name: 'Severe',
                data: severePercent,
                type: 'line',
                connectNulls: false,
                triggerLineEvent: true,
                lineStyle: {
                    width: 1
                },
                itemStyle: {
                    color: severeBGColor
                },
                symbolSize: isPopover ? 0 : 6,
                emphasis: {
                    focus: 'series',
                    blurScope: 'coordinateSystem',
                    scale: 1.3,
                    itemStyle: {
                        borderWidth: 2,
                        borderColor: severeBGColor
                    },
                    lineStyle: {
                        width: 3
                    }
                }
            },
            {
                name: 'Chronic Absence Rate',
                data: chronicPercent,
                type: 'line',
                connectNulls: false,
                triggerLineEvent: true,
                lineStyle: {
                    width: 1
                },
                itemStyle: {
                    color: chronicBGColor
                },
                symbolSize: isPopover ? 0 : 6,
                emphasis: {
                    focus: 'series',
                    blurScope: 'coordinateSystem',
                    scale: 1.3,
                    itemStyle: {
                        borderWidth: 2,
                        borderColor: chronicBGColor
                    },
                    lineStyle: {
                        width: 3
                    }
                }
            }
        ],

        ...((!isPopover) && {
            toolbox: {
                right: 20,
                top: 10,
                feature: {
                    myFullscreen: {
                        show: true,
                        title: 'Fullscreen',
                        icon: 'path://M4.5 11H3v4h4v-1.5H4.5V11zM3 7h1.5V4.5H7V3H3v4zm10.5 6.5H11V15h4v-4h-1.5v2.5zM11 3v1.5h2.5V7H15V3h-4z',
                        onclick: function() {
                            toggleChartFullscreen(chartElement, chart);
                        }
                    },
                    restore: {},
                    saveAsImage: {
                        title: 'Save as Image',
                        name: regionName + '_Weekly_Absence_Metrics',
                        pixelRatio: 2
                    }
                }
            }
        })
        
    };
    
    chart.setOption(option);

    // Build custom HTML legend with Bootstrap tooltips
    // Legend target is inside .chart-legend-container, which is a sibling of the outer chart container
    var outerContainer = chartElement ? chartElement.closest('[data-chart-id]') || chartElement.parentElement : null;
    var legendWrapper = outerContainer ? outerContainer.nextElementSibling : null;
    var legendContainer = legendWrapper ? legendWrapper.querySelector('.chart-legend-target') : null;
    if (legendContainer) {
        var seriesConfig = [
            { name: 'Satisfactory', color: satisfactoryBGColor },
            { name: 'At Risk', color: atRiskBGColor },
            { name: 'Moderate', color: moderateBGColor },
            { name: 'Severe', color: severeBGColor },
            { name: 'Chronic Absence Rate', color: chronicBGColor }
        ];
        buildCustomLegend(legendContainer, seriesConfig, chart, {
            isPopover: isPopover,
            selectedState: option.legend.selected
        });
    }

    // Return chart instance for potential further manipulation
    return chart;
}

// Track chart instances for resize handling
const chartInstances = [];

window.addEventListener('resize', function() {
    chartInstances.forEach(chart => chart.resize());
});

// Handle fullscreen change for chart resize
document.addEventListener('fullscreenchange', () => {
    // Resize all charts when fullscreen state changes
    chartInstances.forEach(chart => {
        if (chart && typeof chart.resize === 'function') {
            chart.resize();
        }
    });
});

// Watch for color mode changes
const colorModeObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
        if (mutation.attributeName === 'data-bs-theme') {
            // Update all charts when color mode changes
            chartInstances.forEach(chart => {
                const isDarkMode = document.documentElement.getAttribute('data-bs-theme') === 'dark';
                const textColor = isDarkMode ? darkModeTextColor : lightModeTextColor;
                const bgColor = isDarkMode ? darkModeBGColor : lightModeBGColor;
                const borderColor = isDarkMode ? darkModeBorderColor : lightModeBorderColor;
                const option = chart.getOption();

                // Update title color
                if (option.title && option.title[0] && option.title[0].textStyle) {
                    option.title[0].textStyle.color = textColor;
                }

                // Update tooltip color
                if (option.tooltip && option.tooltip[0]) {
                    if (option.tooltip[0].textStyle) {
                        option.tooltip[0].textStyle.color = textColor;
                    }
                    option.tooltip[0].backgroundColor = bgColor;
                    option.tooltip[0].borderColor = borderColor;
                }

                // Update legend color (handle single or multiple legends)
                // Note: Custom HTML legends update via CSS; this handles any remaining eCharts legends
                if (option.legend) {
                    option.legend.forEach(legend => {
                        if (legend.textStyle) {
                            legend.textStyle.color = textColor;
                        }
                        if (legend.pageTextStyle) {
                            legend.pageTextStyle.color = textColor;
                        }
                    });
                }

                // Update xAxis colors
                if (option.xAxis && option.xAxis[0]) {
                    if (option.xAxis[0].axisLabel) {
                        option.xAxis[0].axisLabel.color = textColor;
                    }
                    if (option.xAxis[0].nameTextStyle) {
                        option.xAxis[0].nameTextStyle.color = textColor;
                    }
                    if (option.xAxis[0].axisLine && option.xAxis[0].axisLine.lineStyle) {
                        option.xAxis[0].axisLine.lineStyle.color = borderColor;
                    }
                }

                // Update yAxis colors
                if (option.yAxis && option.yAxis[0]) {
                    if (option.yAxis[0].axisLabel) {
                        option.yAxis[0].axisLabel.color = textColor;
                    }
                    if (option.yAxis[0].nameTextStyle) {
                        option.yAxis[0].nameTextStyle.color = textColor;
                    }
                    if (option.yAxis[0].axisLine && option.yAxis[0].axisLine.lineStyle) {
                        option.yAxis[0].axisLine.lineStyle.color = borderColor;
                    }
                    if (option.yAxis[0].splitLine && option.yAxis[0].splitLine.lineStyle) {
                        option.yAxis[0].splitLine.lineStyle.color = isDarkMode ? borderColor : '#ddd';
                    }
                }

                chart.setOption(option);

                // Update cohort chart graphic labels if applicable
                if (typeof updateCohortChartGraphics === 'function') {
                    updateCohortChartGraphics(chart);
                }
            });
        }
    });
});

// Observe the document element for theme attribute changes
colorModeObserver.observe(document.documentElement, { attributes: true });

/* 
// Add print event listeners
window.addEventListener('beforeprint', function() {
    chartInstances.forEach(chart => {
        const option = chart.getOption();
        
        // Save current options for restoration after print
        chart._originalOptions = JSON.parse(JSON.stringify(option));
        
        // Print-specific adjustments
        if (option.title) {
            option.title[0].textStyle.color = '#000000';
        }
        if (option.legend) {
            option.legend[0].textStyle.color = '#000000';
        }

        if (option.xAxis) {
            option.xAxis[0].axisLabel.color = '#000000';
            option.xAxis[0].nameTextStyle.color = '#000000';
        }
        
        if (option.yAxis) {
            option.yAxis[0].axisLabel.color = '#000000';
        }
        
        if (option.tooltip) {
            option.tooltip.backgroundColor = '#ffffff';
            option.tooltip.borderColor = '#000000';
            option.tooltip.textStyle.color = '#000000';
        }
        
        // Make lines darker and thicker for better printing
        if (option.series) {
            option.series.forEach(series => {
                if (series.lineStyle) {
                    series.lineStyle.width = 4; // Thicker lines for print
                }
            });
        }
        
        // Add print-specific grid styling
        if (option.grid) {
            option.grid[0].borderColor = '#dddddd';
            option.grid[0].borderWidth = 1;
        }
        
        chart.setOption(option);
    });
});

window.addEventListener('afterprint', function() {
    chartInstances.forEach(chart => {
        if (chart._originalOptions) {
            // Ensure all original options are restored
            chart.setOption(chart._originalOptions);
            delete chart._originalOptions;
            // Force a resize to ensure proper rendering
            chart.resize();
        }
    });
});
 */



/**
 * Load full page embedded absence chart via AJAX with jQuery
 */
jQuery(document).ready(function($) {
    // Target all containers with data-chart-id attribute
    $('[data-chart-id]').each(function() {
        var chartContainer = $(this);
        var chartURL = chartContainer.data('chart-url');
        var chartId = chartContainer.data('chart-id');

        // Show loading spinner
        chartContainer.html(
            '<div class="text-center py-5 align-self-center">' +
                '<div class="spinner-border text-primary" role="status">' +
                    '<span class="visually-hidden">Loading...</span>' +
                '</div>' +
                '<p class="mt-2">Loading chart data...</p>' +
            '</div>'
        );

        // Get current URL parameters (filters)
        var urlParams = new URLSearchParams(window.location.search);
        var ajaxData = { ajax: 1 };

        // Add filter parameters
        if (urlParams.has('start_date')) {
            ajaxData.start_date = urlParams.get('start_date');
        }
        if (urlParams.has('end_date')) {
            ajaxData.end_date = urlParams.get('end_date');
        }
        if (urlParams.has('in_game_network')) {
            ajaxData.in_game_network = urlParams.get('in_game_network');
        }
        if (urlParams.has('game_level')) {
            ajaxData.game_level = urlParams.get('game_level');
        }

        // Handle multi-select arrays
        var counties = urlParams.getAll('counties[]');
        if (counties.length > 0) {
            ajaxData['counties[]'] = counties;
        }
        var regions = urlParams.getAll('regions[]');
        if (regions.length > 0) {
            ajaxData['regions[]'] = regions;
        }

        // Use jQuery AJAX with explicit async setting
        $.ajax({
            url: chartURL,
            data: ajaxData,
            type: 'GET',
            async: true, // Explicitly set to async mode
            cache: false, // Prevent caching issues
            success: function(response) {
                // Update the container with the response
                chartContainer.html(response);
                
                // Initialize chart if necessary
                if (typeof echarts !== 'undefined') {
                    // Use requestAnimationFrame for better performance
                    requestAnimationFrame(function() {
                        // Find the chart element within this container
                        // The response from page-absence-chart.php should include a div with the ID from chart_id
                        var chartElement = chartContainer.find('[id^="absence-chart-embed"]')[0];
                        if (chartElement) {
                            var chart = echarts.init(chartElement);
                            chart.resize();
                            
                            // Add to tracked instances for resize handling
                            if (typeof chartInstances !== 'undefined') {
                                chartInstances.push(chart);
                            }
                        }
                    });
                }
            },
            error: function(xhr, status, error) {
                chartContainer.html(
                    '<div class="alert alert-danger" role="alert">' +
                        'Failed to load chart data. Please try again later.' +
                    '</div>'
                );
                console.error('Error loading chart:', error);
            }
        });
    });
});


/* 
 * Dynamic popover charts 
 * Load chart data via AJAX when popover is shown
 */

jQuery(document).ready(function($) {
	// Initialize popovers
	const popoverTriggerList = document.querySelectorAll('.trend-change-popover');

	const popoverOptions = {
		trigger: 'manual',
		placement: 'left',
		sanitize: false,
		html: true,
        template: '<div class="popover shadow" role="tooltip"><div class="popover-arrow"></div><div class="popover-body"></div></div>',
		// Add spinner to initial content
		content: '<div class="d-flex justify-content-center align-items-center" style="width: 300px; height: 200px;"><div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div></div>'
	};

	const popoverList = [...popoverTriggerList].map(popoverTriggerEl => {
		const popover = new bootstrap.Popover(popoverTriggerEl, popoverOptions);
		let timeout;
		let isPopoverOver = false;
		
		// Show popover on mouseenter
		popoverTriggerEl.addEventListener('mouseenter', function() {
			clearTimeout(timeout);
			popover.show();
		});
		
		// Handle mouseleave on trigger element
		popoverTriggerEl.addEventListener('mouseleave', function() {
			// Delay hiding to allow moving to popover
			timeout = setTimeout(() => {
				if (!isPopoverOver) {
					popover.hide();
				}
			}, 300);
		});

		// Add click/tap support for touch devices
		popoverTriggerEl.addEventListener('click', function(e) {
			e.preventDefault();
			// Toggle popover on click/tap
			if (this.getAttribute('aria-describedby')) {
				popover.hide();
			} else {
				// Close any other open popovers
				[...popoverList].forEach(p => {
					if (p._element !== popoverTriggerEl) {
						p.hide();
					}
				});
				popover.show();
			}
		});
		
		// Initialize chart when popover is shown
		popoverTriggerEl.addEventListener('shown.bs.popover', function() {
			const popoverId = this.getAttribute('aria-describedby');
			if (!popoverId) return;
			
			const popoverElement = document.getElementById(popoverId);
			if (!popoverElement) return;
			
			// Add mouseenter/mouseleave events to popover
			popoverElement.addEventListener('mouseenter', function() {
				isPopoverOver = true;
				clearTimeout(timeout);
			});
			
			popoverElement.addEventListener('mouseleave', function() {
				isPopoverOver = false;
				timeout = setTimeout(() => {
					popover.hide();
				}, 300);
			});

			
			// Create chart container
			const chartId = `chart-${popoverId}`;
			const chartContainer = document.createElement('div');
			chartContainer.id = chartId;
			chartContainer.className = 'trend-chart';
			chartContainer.style.width = '300px';
			chartContainer.style.height = '200px';

			// Get chart URL from the trigger element (not from the container)
			const chartURL = popoverTriggerEl.getAttribute('data-chart-url');
			const chartTargetId = popoverTriggerEl.getAttribute('data-chart-target');

			// Get popover body and append chart container
			const popoverBody = popoverElement.querySelector('.popover-body');
			popoverBody.innerHTML = '';
			popoverBody.appendChild(chartContainer);

			// Add loading indicator
			jQuery(chartContainer).html(
				'<div class="text-center py-5">' +
					'<div class="spinner-border text-primary" role="status">' +
						'<span class="visually-hidden">Loading...</span>' +
					'</div>' +
					'<p class="mt-2">Loading chart data...</p>' +
				'</div>'
			);

			// Get current URL parameters (filters)
			var urlParams = new URLSearchParams(window.location.search);
			var ajaxData = { ajax: 1 };

			// Add filter parameters
			if (urlParams.has('start_date')) {
				ajaxData.start_date = urlParams.get('start_date');
			}
			if (urlParams.has('end_date')) {
				ajaxData.end_date = urlParams.get('end_date');
			}
			if (urlParams.has('in_game_network')) {
				ajaxData.in_game_network = urlParams.get('in_game_network');
			}
			if (urlParams.has('game_level')) {
				ajaxData.game_level = urlParams.get('game_level');
			}

			// Handle multi-select arrays
			var counties = urlParams.getAll('counties[]');
			if (counties.length > 0) {
				ajaxData['counties[]'] = counties;
			}
			var regions = urlParams.getAll('regions[]');
			if (regions.length > 0) {
				ajaxData['regions[]'] = regions;
			}

			// Use jQuery AJAX to load chart data
			jQuery.ajax({
				url: chartURL,
				data: ajaxData,
				type: 'GET',
				async: true,
				cache: false,
				success: function(response) {
					jQuery(chartContainer).html(response);
					
					// Initialize chart if necessary
					if (typeof echarts !== 'undefined') {
						requestAnimationFrame(function() {
							const chartElement = document.querySelector(chartTargetId);
							if (chartElement) {
								const chart = echarts.init(chartElement);
								chart.resize();
							}
						});
					}
				},
				error: function(xhr, status, error) {
					jQuery(chartContainer).html(
						'<div class="alert alert-danger" role="alert">' +
							'Failed to load chart data. Please try again later.' +
						'</div>'
					);
					console.error('Error loading chart:', error);
				}
			});	
			
		});
		
		return popover;
	});
});